import generateRandomString from '~/utils/generateRandomString';
import { getCurrencyCodeByRegionCode } from '~/utils/getCurrencyCodeByRegionCode';

/**
 * Subset of `voucher_rewards` used for regional clone hints.
 * Promo clone matches rewards by the selected region’s currency (see clone voucher OpenAPI), not product keys.
 */
export type VoucherCloneRewardRegionInput = {
  reward_amount: number;
  currency_code?: string;
};

function effectiveVoucherRewardCurrency(
  reward: VoucherCloneRewardRegionInput,
  voucherDefaultCurrency?: string,
): string | null {
  const fromReward = reward.currency_code?.trim();
  if (fromReward) {
    return fromReward;
  }
  const fallback = voucherDefaultCurrency?.trim();
  return fallback || null;
}

/**
 * Rewards whose currency matches the region’s currency (reward `currency_code`, else voucher default).
 * Aligns with promo: regional clone operates on voucher_rewards for that region’s currency.
 */
export function filterVoucherRewardsForRegion(
  rewards: ReadonlyArray<VoucherCloneRewardRegionInput> | undefined,
  regionCode: string,
  validRegionCodes: ReadonlySet<string>,
  voucherDefaultCurrency?: string,
): Array<VoucherCloneRewardRegionInput> {
  if (!rewards?.length) {
    return [];
  }
  const r = regionCode.trim();
  if (!r || !validRegionCodes.has(r)) {
    return [];
  }
  const regionCurrency = getCurrencyCodeByRegionCode(r);
  if (!regionCurrency) {
    return [];
  }
  return rewards.filter((reward) => {
    const eff = effectiveVoucherRewardCurrency(reward, voucherDefaultCurrency);
    return eff === regionCurrency;
  });
}

export type VoucherRegionRewardSummary = {
  kind: 'none' | 'single' | 'multiple';
  /** Set when `kind === 'single'` (amount and optional currency). */
  amountLabel?: string;
};

export function summarizeVoucherRewardsForRegion(
  rewards: ReadonlyArray<VoucherCloneRewardRegionInput> | undefined,
  regionCode: string,
  validRegionCodes: ReadonlySet<string>,
  voucherDefaultCurrency?: string,
): VoucherRegionRewardSummary {
  const list = filterVoucherRewardsForRegion(rewards, regionCode, validRegionCodes, voucherDefaultCurrency);
  if (list.length === 0) {
    return { kind: 'none' };
  }
  if (list.length === 1) {
    const row = list[0];
    const num = Number(row.reward_amount);
    const cur = row.currency_code?.trim();
    const regionCur = getCurrencyCodeByRegionCode(regionCode.trim());
    const displayCur = cur || regionCur || '';
    const amountLabel = Number.isFinite(num) ? (displayCur ? `${num} ${displayCur}` : String(num)) : '';
    return { kind: 'single', amountLabel };
  }
  return { kind: 'multiple' };
}

/** Matches suffixes added by `buildSuggestedClonedVoucherName` (`-{8 alphanumeric}`). */
const TRAILING_CLONE_SUFFIX_RE = /^(.+)-[A-Za-z0-9]{8}$/;

/**
 * Removes one or more trailing clone suffixes so re-cloning suggests
 * `{root}-{newSuffix}` instead of `{root}-{old}-{old2}-{newSuffix}`.
 */
function voucherNameRootForCloneSuggestion(trimmedName: string): string {
  let current = trimmedName;
  for (;;) {
    const match = current.match(TRAILING_CLONE_SUFFIX_RE);
    if (!match) {
      return current;
    }
    current = match[1];
  }
}

/** Default suggested name when cloning: `{root without prior clone suffixes}-{random suffix}`. */
export function buildSuggestedClonedVoucherName(baseVoucherName: string): string {
  const base = voucherNameRootForCloneSuggestion(baseVoucherName.trim());
  if (!base) {
    return '';
  }
  return `${base}-${generateRandomString(8)}`;
}
